import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart3,
  BookOpen,
  Eye,
  Link2,
  Loader2,
  MessageCircle,
  Pause,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Route,
  Smartphone,
  Stethoscope,
  Trash2,
  Search,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import DeepLinkCampaignFormModal from '../../components/MasterAdmin/deep-links/DeepLinkCampaignFormModal';
import DeepLinkCampaignViewModal from '../../components/MasterAdmin/deep-links/DeepLinkCampaignViewModal';
import DeepLinkCodeInspectModal, {
  DeepLinkCheckerHelpTooltip,
  DeepLinkInspectSummaryTooltip,
} from '../../components/MasterAdmin/deep-links/DeepLinkCodeInspectModal';
import DeepLinkRouteFormModal from '../../components/MasterAdmin/deep-links/DeepLinkRouteFormModal';
import DeepLinkAppLinksModal from '../../components/MasterAdmin/deep-links/DeepLinkAppLinksModal';
import DeepLinkDoctorSeedPanel from '../../components/MasterAdmin/deep-links/DeepLinkDoctorSeedPanel';
import DeepLinkBlogSeedPanel from '../../components/MasterAdmin/deep-links/DeepLinkBlogSeedPanel';
import {
  ActionMenuDivider,
  ActionMenuItem,
  ActionsDropdownMenu,
} from '../../components/MasterAdmin/deep-links/ActionsDropdownMenu';
import CopyLinkButton from '../../components/MasterAdmin/deep-links/CopyLinkButton';
import ConfirmDialog from '../../components/MasterAdmin/ui/ConfirmDialog';
import {
  dlBtnGhostClass,
  dlBtnPrimaryClass,
  dlBtnSecondaryClass,
  dlCampaignCardClass,
  dlCardClass,
  dlInputClass,
  dlPageBody,
  dlPageHeader,
  dlPageWrap,
  dlStatCardClass,
  dlTabBarClass,
  dlTabButtonClass,
  dlTableCellClass,
  dlTableHeadClass,
  dlTableRowClass,
  dlTableWrapClass,
} from '../../components/MasterAdmin/deep-links/deepLinkUi';
import type {
  DeepLinkAnalyticsSummary,
  DeepLinkCampaign,
  DeepLinkCampaignFormValues,
  DeepLinkCampaignAnalyticsRow,
  DeepLinkCampaignLeaderboardRow,
  DeepLinkFunnel,
  DeepLinkRoute,
  DeepLinkRouteFormValues,
  DeepLinkEnvironmentConfig,
  DeepLinkScreenCatalog,
  DeepLinkScreenCatalogItem,
  DeepLinkTargetAppKey,
  DeepLinkWhatsAppUsage,
  DeepLinkShortCodeLookup,
} from '../../models/MasterAdmin/deepLink';
import {
  DEEP_LINK_TARGET_APP_OPTIONS,
} from '../../models/MasterAdmin/deepLink';
import { RouteSeedStatusBadge } from '../../components/MasterAdmin/deep-links/RouteSeedStatusBadge';
import {
  activateDeepLinkCampaign,
  createDeepLinkCampaign,
  createDeepLinkRoute,
  deleteDeepLinkCampaign,
  deleteDeepLinkRoute,
  fetchDeepLinkAnalyticsByCampaign,
  fetchDeepLinkAnalyticsSummary,
  fetchDeepLinkCampaignsPage,
  fetchDeepLinkConfig,
  fetchDeepLinkFunnel,
  fetchDeepLinkLeaderboard,
  fetchDeepLinkRoutes,
  fetchDeepLinkScreenCatalog,
  lookupDeepLinkShortCode,
  pauseDeepLinkCampaign,
  seedDefaultDeepLinkRoutes,
  syncDeepLinkScreenRegistry,
  updateDeepLinkCampaign,
  updateDeepLinkRoute,
} from '../../services/MasterAdmin/deepLinkService';

type Tab = 'campaigns' | 'doctors' | 'blogs' | 'routes' | 'analytics';
type CampaignScope = 'all' | 'manual' | 'doctor' | 'blog';

const CAMPAIGN_PAGE_SIZE = 40;

function campaignListParams(scope: CampaignScope, search: string, offset: number) {
  const q = search.trim();
  const params: {
    limit: number;
    offset: number;
    search?: string;
    entityType?: string;
    excludeEntityTypes?: string;
  } = {
    limit: CAMPAIGN_PAGE_SIZE,
    offset,
  };
  if (q) params.search = q;
  if (scope === 'doctor') params.entityType = 'doctor';
  if (scope === 'blog') params.entityType = 'blog';
  if (scope === 'manual') params.excludeEntityTypes = 'doctor,blog';
  return params;
}

type ConfirmState =
  | { kind: 'delete-campaign'; campaign: DeepLinkCampaign }
  | { kind: 'pause-campaign'; campaign: DeepLinkCampaign }
  | { kind: 'delete-route'; route: DeepLinkRoute }
  | null;

function pct(value: number) {
  return `${(value * 100).toFixed(1)}%`;
}

function formatWhatsAppUsageSummary(usage?: DeepLinkWhatsAppUsage | null) {
  if (!usage?.used) return null;
  const parts: string[] = [];
  if (usage.notifications.length) parts.push(`${usage.notifications.length} notification(s)`);
  if (usage.templates.length) parts.push(`${usage.templates.length} template(s)`);
  if (usage.campaigns.length) parts.push(`${usage.campaigns.length} WhatsApp campaign(s)`);
  return parts.join(', ');
}

function WhatsAppUsageBadge({ usage }: { usage?: DeepLinkWhatsAppUsage | null }) {
  if (!usage?.used) return null;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"
      title={formatWhatsAppUsageSummary(usage) || 'Used in WhatsApp'}
    >
      <MessageCircle className="h-3 w-3" />
      WhatsApp ({usage.total})
    </span>
  );
}

const DeepLinksManagementPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('campaigns');
  const [loading, setLoading] = useState(true);
  const [routes, setRoutes] = useState<DeepLinkRoute[]>([]);
  const [campaigns, setCampaigns] = useState<DeepLinkCampaign[]>([]);
  const [campaignsTotal, setCampaignsTotal] = useState(0);
  const [campaignsHasMore, setCampaignsHasMore] = useState(true);
  const [campaignsLoadingMore, setCampaignsLoadingMore] = useState(false);
  const [campaignScope, setCampaignScope] = useState<CampaignScope>('all');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const campaignsRef = useRef<DeepLinkCampaign[]>([]);
  const campaignsHasMoreRef = useRef(true);
  const campaignsLoadingMoreRef = useRef(false);
  const campaignScopeRef = useRef<CampaignScope>('all');
  const debouncedSearchRef = useRef('');
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const [summary, setSummary] = useState<DeepLinkAnalyticsSummary | null>(null);
  const [funnel, setFunnel] = useState<DeepLinkFunnel | null>(null);
  const [leaderboard, setLeaderboard] = useState<DeepLinkCampaignLeaderboardRow[]>([]);
  const [campaignAnalytics, setCampaignAnalytics] = useState<
    Record<string, DeepLinkCampaignAnalyticsRow>
  >({});
  const [search, setSearch] = useState('');
  const [codeLookupInput, setCodeLookupInput] = useState('');
  const [codeLookupBusy, setCodeLookupBusy] = useState(false);
  const [codeLookup, setCodeLookup] = useState<DeepLinkShortCodeLookup | null>(null);
  const [codeLookupModalOpen, setCodeLookupModalOpen] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);

  const [campaignFormOpen, setCampaignFormOpen] = useState(false);
  const [campaignFormMode, setCampaignFormMode] = useState<'create' | 'edit'>('create');
  const [editingCampaign, setEditingCampaign] = useState<DeepLinkCampaign | null>(null);
  const [viewCampaign, setViewCampaign] = useState<DeepLinkCampaign | null>(null);

  const [routeFormOpen, setRouteFormOpen] = useState(false);
  const [routeFormMode, setRouteFormMode] = useState<'create' | 'edit'>('create');
  const [editingRoute, setEditingRoute] = useState<DeepLinkRoute | null>(null);
  const [linkConfig, setLinkConfig] = useState<DeepLinkEnvironmentConfig | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);
  const [appLinksOpen, setAppLinksOpen] = useState(false);
  const [registryAppKey, setRegistryAppKey] = useState<DeepLinkTargetAppKey>('user');
  const [screenCatalog, setScreenCatalog] = useState<DeepLinkScreenCatalog | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);

  const closeMenu = useCallback(() => setOpenMenuKey(null), []);

  const runCodeLookup = useCallback(async () => {
    const q = codeLookupInput.trim();
    if (!q) {
      toast.error('Paste a /u or /p short code or full URL');
      return;
    }
    setCodeLookupBusy(true);
    try {
      const result = await lookupDeepLinkShortCode(q);
      setCodeLookup(result);
      setCodeLookupModalOpen(true);
      if (!result.valid) {
        toast.error(result.message || 'Code not found');
      }
    } catch (err) {
      setCodeLookup(null);
      setCodeLookupModalOpen(false);
      toast.error(err instanceof Error ? err.message : 'Lookup failed');
    } finally {
      setCodeLookupBusy(false);
    }
  }, [codeLookupInput]);

  const renderCampaignAnalysis = (campaignId: string) => {
    const row = campaignAnalytics[campaignId];
    const clicks = row?.clicks ?? 0;
    const navOk = row?.navigationSuccesses ?? 0;
    const mismatches = row?.navigationMismatches ?? 0;
    const navErrors = row?.navigationErrors ?? 0;
    const errorTotal = mismatches + navErrors;

    return (
      <div className="mt-2 space-y-1 text-xs">
        <div className="flex flex-wrap gap-2 text-gray-600 dark:text-gray-300">
          <span className="rounded-md bg-gray-100 px-2 py-0.5 dark:bg-gray-800">
            Clicks {clicks}
          </span>
          <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
            Nav OK {navOk}
          </span>
          <span
            className={`rounded-md px-2 py-0.5 ${
              errorTotal > 0
                ? 'bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
            }`}
          >
            Errors {errorTotal}
          </span>
        </div>
        {errorTotal > 0 ? (
          <p className="text-amber-700 dark:text-amber-300">
            {mismatches} mismatch{mismatches === 1 ? '' : 'es'}
            {navErrors ? `, ${navErrors} error${navErrors === 1 ? '' : 's'}` : ''} — open View for details
          </p>
        ) : (
          <p className="text-gray-500 dark:text-gray-400">No navigation errors for this link</p>
        )}
      </div>
    );
  };

  const filteredCampaigns = campaigns;

  useEffect(() => {
    campaignsRef.current = campaigns;
  }, [campaigns]);

  useEffect(() => {
    campaignsHasMoreRef.current = campaignsHasMore;
  }, [campaignsHasMore]);

  useEffect(() => {
    campaignScopeRef.current = campaignScope;
  }, [campaignScope]);

  useEffect(() => {
    debouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  const loadRoutes = useCallback(async () => {
    const rows = await fetchDeepLinkRoutes(true);
    setRoutes(rows);
    return rows;
  }, []);

  const fetchCampaignPage = useCallback(
    async (offset: number, scope: CampaignScope, q: string) => {
      return fetchDeepLinkCampaignsPage(campaignListParams(scope, q, offset));
    },
    []
  );

  const applyCampaignPage = useCallback(
    (page: { items: DeepLinkCampaign[]; total: number }, mode: 'replace' | 'append') => {
      setCampaignsTotal(page.total);
      setCampaigns((prev) => {
        const next =
          mode === 'replace'
            ? page.items
            : (() => {
                const seen = new Set(prev.map((c) => c.id));
                const merged = [...prev];
                for (const item of page.items) {
                  if (!seen.has(item.id)) {
                    seen.add(item.id);
                    merged.push(item);
                  }
                }
                return merged;
              })();
        // Stop when we got a short page or already have everything — prevents end flicker loops
        const hasMore = next.length < page.total && page.items.length >= CAMPAIGN_PAGE_SIZE;
        campaignsHasMoreRef.current = hasMore;
        campaignsRef.current = next;
        setCampaignsHasMore(hasMore);
        return next;
      });
    },
    []
  );

  const loadCampaigns = useCallback(async () => {
    const page = await fetchCampaignPage(0, campaignScope, debouncedSearch);
    applyCampaignPage(page, 'replace');
    return page.items;
  }, [fetchCampaignPage, campaignScope, debouncedSearch, applyCampaignPage]);

  const loadMoreCampaigns = useCallback(async () => {
    if (!campaignsHasMoreRef.current || campaignsLoadingMoreRef.current) return;
    campaignsLoadingMoreRef.current = true;
    setCampaignsLoadingMore(true);
    try {
      const offset = campaignsRef.current.length;
      const page = await fetchCampaignPage(
        offset,
        campaignScopeRef.current,
        debouncedSearchRef.current
      );
      if (!page.items.length) {
        campaignsHasMoreRef.current = false;
        setCampaignsHasMore(false);
        setCampaignsTotal(page.total);
        return;
      }
      applyCampaignPage(page, 'append');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to load more campaigns');
    } finally {
      campaignsLoadingMoreRef.current = false;
      setCampaignsLoadingMore(false);
    }
  }, [fetchCampaignPage, applyCampaignPage]);

  const loadAnalytics = useCallback(async () => {
    const [s, f, l, byCampaign] = await Promise.all([
      fetchDeepLinkAnalyticsSummary(),
      fetchDeepLinkFunnel(),
      fetchDeepLinkLeaderboard({ limit: 10 }),
      fetchDeepLinkAnalyticsByCampaign().catch(() => ({})),
    ]);
    setSummary(s);
    setFunnel(f);
    setLeaderboard(l);
    setCampaignAnalytics(byCampaign);
  }, []);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [, , , config] = await Promise.all([
        loadRoutes(),
        fetchCampaignPage(0, campaignScopeRef.current, debouncedSearchRef.current).then((page) => {
          applyCampaignPage(page, 'replace');
          return page.items;
        }),
        loadAnalytics(),
        fetchDeepLinkConfig().catch(() => null),
      ]);
      setLinkConfig(config);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to load deep links');
    } finally {
      setLoading(false);
    }
  }, [loadRoutes, fetchCampaignPage, loadAnalytics, applyCampaignPage]);

  // Initial load only once
  const didInitialLoad = useRef(false);
  useEffect(() => {
    if (didInitialLoad.current) return;
    didInitialLoad.current = true;
    void loadAll();
  }, [loadAll]);

  // Debounce search for server-side filtering
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  // Reload first page when filter/search changes (skip first mount — loadAll handles it)
  const skipFilterReload = useRef(true);
  useEffect(() => {
    if (skipFilterReload.current) {
      skipFilterReload.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        campaignsLoadingMoreRef.current = false;
        setCampaignsLoadingMore(false);
        const page = await fetchCampaignPage(0, campaignScope, debouncedSearch);
        if (cancelled) return;
        applyCampaignPage(page, 'replace');
      } catch (e: any) {
        if (!cancelled) {
          toast.error(e?.response?.data?.message || 'Failed to load campaigns');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campaignScope, debouncedSearch, fetchCampaignPage, applyCampaignPage]);

  // Stable infinite-scroll observer — do NOT reconnect on every page append
  useEffect(() => {
    if (tab !== 'campaigns') return;
    const node = loadMoreSentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        if (!visible) return;
        if (!campaignsHasMoreRef.current || campaignsLoadingMoreRef.current) return;
        void loadMoreCampaigns();
      },
      { root: null, rootMargin: '64px', threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [tab, loadMoreCampaigns]);

  const loadScreenCatalog = useCallback(async (targetAppKey: DeepLinkTargetAppKey) => {
    setCatalogLoading(true);
    try {
      const catalog = await fetchDeepLinkScreenCatalog(targetAppKey);
      setScreenCatalog(catalog);
    } catch {
      setScreenCatalog(null);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab !== 'routes') return;
    void loadScreenCatalog(registryAppKey);
  }, [tab, registryAppKey, loadScreenCatalog, routes]);

  const registryScreens = useMemo(() => {
    if (!screenCatalog) return [] as DeepLinkScreenCatalogItem[];
    return screenCatalog.services.flatMap(
      (svc) => screenCatalog.screensByService[svc.key] || []
    );
  }, [screenCatalog]);

  const handleCreateCampaign = async (values: DeepLinkCampaignFormValues) => {
    await createDeepLinkCampaign(values);
    toast.success('Campaign created');
    await loadCampaigns();
  };

  const handleUpdateCampaign = async (values: DeepLinkCampaignFormValues) => {
    if (!editingCampaign) return;
    await updateDeepLinkCampaign(editingCampaign.id, values);
    toast.success('Campaign updated');
    await loadCampaigns();
  };

  const handleDeleteCampaign = async (campaign: DeepLinkCampaign, force = false) => {
    try {
      setActionId(campaign.id);
      await deleteDeepLinkCampaign(campaign.id, { force });
      toast.success('Campaign deleted');
      await loadCampaigns();
    } catch (e: any) {
      if (e?.response?.status === 409 && !force) {
        toast.error('This deep link is used in WhatsApp. Confirm again to force delete.');
        return;
      }
      toast.error(e?.response?.data?.message || 'Delete failed');
    } finally {
      setActionId(null);
    }
  };

  const handleActivate = async (campaign: DeepLinkCampaign) => {
    try {
      setActionId(campaign.id);
      await activateDeepLinkCampaign(campaign.id);
      toast.success('Campaign activated');
      await loadCampaigns();
    } finally {
      setActionId(null);
    }
  };

  const handlePause = async (campaign: DeepLinkCampaign) => {
    try {
      setActionId(campaign.id);
      await pauseDeepLinkCampaign(campaign.id);
      toast.success('Campaign paused');
      await loadCampaigns();
    } finally {
      setActionId(null);
    }
  };

  const handleCreateRoute = async (values: DeepLinkRouteFormValues) => {
    await createDeepLinkRoute(values);
    toast.success('Route created');
    await loadRoutes();
  };

  const handleUpdateRoute = async (values: DeepLinkRouteFormValues) => {
    if (!editingRoute) return;
    await updateDeepLinkRoute(editingRoute.id, values);
    toast.success('Route updated');
    await loadRoutes();
  };

  const handleDeleteRoute = async (route: DeepLinkRoute) => {
    await deleteDeepLinkRoute(route.id);
    toast.success('Route deleted');
    await loadRoutes();
  };

  const handleConfirmAction = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    try {
      if (confirm.kind === 'delete-campaign') {
        await handleDeleteCampaign(confirm.campaign, Boolean(confirm.campaign.whatsappUsage?.used));
      } else if (confirm.kind === 'pause-campaign') {
        await handlePause(confirm.campaign);
      } else if (confirm.kind === 'delete-route') {
        await handleDeleteRoute(confirm.route);
      }
      setConfirm(null);
    } catch {
      /* errors handled in handlers */
    } finally {
      setConfirmBusy(false);
    }
  };

  const renderCampaignActions = (campaign: DeepLinkCampaign) => {
    const menuKey = `campaign:${campaign.id}`;
    const busy = actionId === campaign.id;

    return (
      <ActionsDropdownMenu
        open={openMenuKey === menuKey}
        onToggle={() => setOpenMenuKey((key) => (key === menuKey ? null : menuKey))}
        onClose={closeMenu}
      >
        <ActionMenuItem
          icon={<Eye className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
          label="View"
          onClick={() => {
            closeMenu();
            setViewCampaign(campaign);
          }}
        />
        <ActionMenuItem
          icon={<Pencil className="h-4 w-4 text-amber-600 dark:text-amber-400" />}
          label="Edit"
          onClick={() => {
            closeMenu();
            setCampaignFormMode('edit');
            setEditingCampaign(campaign);
            setCampaignFormOpen(true);
          }}
        />
        {campaign.status === 'ACTIVE' ? (
          <ActionMenuItem
            icon={<Pause className="h-4 w-4 text-gray-600 dark:text-gray-300" />}
            label="Pause"
            disabled={busy}
            onClick={() => {
              closeMenu();
              setConfirm({ kind: 'pause-campaign', campaign });
            }}
          />
        ) : (
          <ActionMenuItem
            icon={<Play className="h-4 w-4 text-green-600 dark:text-green-400" />}
            label="Activate"
            disabled={busy}
            onClick={() => {
              closeMenu();
              void handleActivate(campaign);
            }}
          />
        )}
        <ActionMenuDivider />
        <ActionMenuItem
          icon={<Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />}
          label="Delete"
          tone="danger"
          disabled={busy}
          onClick={() => {
            closeMenu();
            setConfirm({ kind: 'delete-campaign', campaign });
          }}
        />
      </ActionsDropdownMenu>
    );
  };

  const renderRouteActions = (route: DeepLinkRoute) => {
    const menuKey = `route:${route.id}`;

    return (
      <ActionsDropdownMenu
        open={openMenuKey === menuKey}
        onToggle={() => setOpenMenuKey((key) => (key === menuKey ? null : menuKey))}
        onClose={closeMenu}
      >
        <ActionMenuItem
          icon={<Pencil className="h-4 w-4 text-amber-600 dark:text-amber-400" />}
          label="Edit"
          onClick={() => {
            closeMenu();
            setRouteFormMode('edit');
            setEditingRoute(route);
            setRouteFormOpen(true);
          }}
        />
        <ActionMenuDivider />
        <ActionMenuItem
          icon={<Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />}
          label="Delete"
          tone="danger"
          onClick={() => {
            closeMenu();
            setConfirm({ kind: 'delete-route', route });
          }}
        />
      </ActionsDropdownMenu>
    );
  };

  const handleSeedRoutes = async () => {
    const result = await seedDefaultDeepLinkRoutes();
    toast.success(`Seeded ${result.created} new routes, updated ${result.updated} existing`);
    await loadRoutes();
    if (tab === 'routes') await loadScreenCatalog(registryAppKey);
  };

  const handleSyncRegistry = async () => {
    const result = await syncDeepLinkScreenRegistry();
    const skipped = result.skipped ? `, ${result.skipped} skipped` : '';
    const partial =
      result.errors?.length ? ` (${result.errors.length} screens had warnings — check server logs)` : '';
    toast.success(`Synced registry: ${result.created} created, ${result.updated} updated${skipped}${partial}`);
    await loadRoutes();
    if (tab === 'routes') await loadScreenCatalog(registryAppKey);
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#128C7E]" />
      </div>
    );
  }

  return (
    <div className={`${dlPageWrap} mx-auto w-full max-w-[1400px]`}>
      <div className={dlPageHeader}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Deep links</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage campaigns, seed doctor and blog links, and track opens across User and Plus apps.
            </p>
            {linkConfig ? (
              <p className="mt-2 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200">
                <span
                  className={`h-2 w-2 rounded-full ${
                    linkConfig.isProduction ? 'bg-emerald-500' : 'bg-amber-500'
                  }`}
                />
                Link base: <code className="font-mono">{linkConfig.domain}</code>
                {linkConfig.isProduction ? ' (production)' : ' (local testing)'}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setAppLinksOpen(true)}
              className={dlBtnSecondaryClass}
            >
              <Smartphone className="h-4 w-4" /> App store links
            </button>
            <button type="button" onClick={() => void loadAll()} className={dlBtnGhostClass}>
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div className={dlTabBarClass}>
        {[
          { id: 'campaigns' as Tab, label: 'Campaigns', icon: Link2 },
          { id: 'doctors' as Tab, label: 'Doctors', icon: Stethoscope },
          { id: 'blogs' as Tab, label: 'Blogs', icon: BookOpen },
          { id: 'routes' as Tab, label: 'Routes', icon: Route },
          { id: 'analytics' as Tab, label: 'Analytics', icon: BarChart3 },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={dlTabButtonClass(tab === id)}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      <div className={`${dlPageBody} space-y-4`}>
        {summary && (tab === 'campaigns' || tab === 'analytics') ? (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
            {[
              ['Clicks', summary.totalClicks],
              ['Web opens', summary.webOpens ?? summary.clicksByPlatform?.WEB ?? 0],
              ['Android', summary.androidClicks ?? summary.clicksByPlatform?.ANDROID ?? 0],
              ['iOS', summary.iosClicks ?? summary.clicksByPlatform?.IOS ?? 0],
              ['App opens', summary.appClicks ?? summary.appOpens ?? summary.clicksByPlatform?.APP ?? 0],
              ['Installs', summary.installs],
              ['Logins', summary.logins],
            ].map(([label, value]) => (
              <div key={String(label)} className={dlStatCardClass}>
                <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                <p className="mt-1 text-xl font-bold text-gray-900 dark:text-white">{value}</p>
              </div>
            ))}
          </div>
        ) : null}

      {tab === 'doctors' ? <DeepLinkDoctorSeedPanel /> : null}
      {tab === 'blogs' ? <DeepLinkBlogSeedPanel /> : null}

      {tab === 'campaigns' ? (
        <div className="space-y-4">
          <div className={`${dlCardClass} space-y-3 p-4`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  Check a WhatsApp /u/ or /p/ code
                </p>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  Paste a User or Plus short link, or just the code. Check opens full inspect
                  details without recording a click.
                </p>
              </div>
              <DeepLinkCheckerHelpTooltip />
            </div>
            <form
              className="flex flex-wrap items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                void runCodeLookup();
              }}
            >
              <div className="relative min-w-[220px] flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  value={codeLookupInput}
                  onChange={(e) => setCodeLookupInput(e.target.value)}
                  placeholder="wtu2kefh or https://vedika.health/u/… or /p/…"
                  className={`w-full pl-9 ${dlInputClass}`}
                />
              </div>
              <button type="submit" disabled={codeLookupBusy} className={dlBtnPrimaryClass}>
                {codeLookupBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Check
              </button>
            </form>
            {codeLookup ? (
              <div
                className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-xs ${
                  codeLookup.valid
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100'
                    : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100'
                }`}
              >
                <p className="font-semibold">
                  {codeLookup.valid ? 'Valid' : 'Not live'} · {codeLookup.kind} ·{' '}
                  <span className="font-mono">
                    {codeLookup.parsed?.shortCode || codeLookup.shortCode || '—'}
                  </span>
                </p>
                <DeepLinkInspectSummaryTooltip result={codeLookup} />
                <button
                  type="button"
                  className="ml-auto font-medium underline"
                  onClick={() => setCodeLookupModalOpen(true)}
                >
                  View details
                </button>
              </div>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search campaigns..."
                className={`w-full max-w-sm ${dlInputClass}`}
              />
              {(
                [
                  { id: 'all' as const, label: 'All' },
                  { id: 'manual' as const, label: 'Manual' },
                  { id: 'doctor' as const, label: 'Doctors' },
                  { id: 'blog' as const, label: 'Blogs' },
                ] as const
              ).map((scope) => (
                <button
                  key={scope.id}
                  type="button"
                  onClick={() => setCampaignScope(scope.id)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                    campaignScope === scope.id
                      ? 'bg-[#128C7E] text-white'
                      : 'border border-gray-200 bg-white text-gray-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300'
                  }`}
                >
                  {scope.label}
                </button>
              ))}
              <span className="text-xs text-gray-500">
                Showing {filteredCampaigns.length}
                {campaignsTotal ? ` of ${campaignsTotal}` : ''}
                {campaignsHasMore ? ' · scroll for more' : ''}
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                setCampaignFormMode('create');
                setEditingCampaign(null);
                setCampaignFormOpen(true);
              }}
              className={`${dlBtnPrimaryClass} w-full sm:w-auto`}
            >
              <Plus className="h-4 w-4" /> New campaign
            </button>
          </div>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {filteredCampaigns.map((campaign) => (
              <article key={campaign.id} className={dlCampaignCardClass}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="truncate font-semibold text-gray-900 dark:text-white">
                      {campaign.name}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {campaign.routeSlug} · {campaign.status}
                    </p>
                    <div className="mt-1">
                      <WhatsAppUsageBadge usage={campaign.whatsappUsage} />
                    </div>
                  </div>
                  {renderCampaignActions(campaign)}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-300">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Clicks</span>
                    <p className="font-medium">
                      {campaignAnalytics[campaign.id]?.clicks ?? campaign.totalClicks}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Installs</span>
                    <p className="font-medium">
                      {campaignAnalytics[campaign.id]?.installs ?? campaign.totalInstalls ?? 0}
                    </p>
                  </div>
                </div>
                {renderCampaignAnalysis(campaign.id)}
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-gray-50 p-2 dark:bg-gray-900/50">
                  <p className="min-w-0 flex-1 truncate text-xs text-blue-700 dark:text-blue-300">
                    {campaign.shortUrl}
                  </p>
                  <CopyLinkButton text={campaign.shortUrl} label="Copy" showToast />
                </div>
              </article>
            ))}
            {!filteredCampaigns.length ? (
              <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400 md:hidden">
                No campaigns yet. Create one to generate short links and QR codes.
              </p>
            ) : null}
          </div>

          {/* Desktop table */}
          <div className={`${dlTableWrapClass} hidden md:block`}>
            <table className="min-w-full text-sm">
              <thead className={dlTableHeadClass}>
                <tr>
                  <th className="px-4 py-3">Campaign</th>
                  <th className="px-4 py-3">Route</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Short link</th>
                  <th className="px-4 py-3">Analysis</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCampaigns.map((campaign) => {
                  const analytics = campaignAnalytics[campaign.id];
                  const errorTotal =
                    (analytics?.navigationMismatches || 0) + (analytics?.navigationErrors || 0);
                  return (
                  <tr key={campaign.id} className={dlTableRowClass}>
                    <td className={`${dlTableCellClass} font-medium`}>
                      <div>
                        <p>{campaign.name}</p>
                        <p className="text-xs font-normal text-gray-500 dark:text-gray-400">
                          {campaign.targetPlatforms?.join(', ')}
                        </p>
                        <div className="mt-1">
                          <WhatsAppUsageBadge usage={campaign.whatsappUsage} />
                        </div>
                      </div>
                    </td>
                    <td className={dlTableCellClass}>{campaign.routeSlug}</td>
                    <td className={dlTableCellClass}>{campaign.status}</td>
                    <td className={dlTableCellClass}>
                      <div className="flex max-w-[240px] items-center gap-1">
                        <span className="min-w-0 flex-1 truncate text-blue-700 dark:text-blue-400">
                          {campaign.shortUrl}
                        </span>
                        <CopyLinkButton text={campaign.shortUrl} label="Copy" showToast />
                      </div>
                    </td>
                    <td className={dlTableCellClass}>
                      <div className="min-w-[180px] space-y-1 text-xs">
                        <p>
                          Clicks{' '}
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {analytics?.clicks ?? campaign.totalClicks ?? 0}
                          </span>
                          {' · '}
                          Installs{' '}
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {analytics?.installs ?? campaign.totalInstalls ?? 0}
                          </span>
                          {' · '}
                          Nav OK{' '}
                          <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                            {analytics?.navigationSuccesses ?? 0}
                          </span>
                        </p>
                        <p
                          className={
                            errorTotal > 0
                              ? 'font-medium text-amber-700 dark:text-amber-300'
                              : 'text-gray-500 dark:text-gray-400'
                          }
                        >
                          {errorTotal > 0
                            ? `${analytics?.navigationMismatches || 0} mismatch · ${analytics?.navigationErrors || 0} error — View for details`
                            : 'No navigation errors'}
                        </p>
                      </div>
                    </td>
                    <td className={dlTableCellClass}>
                      <div className="flex justify-end">{renderCampaignActions(campaign)}</div>
                    </td>
                  </tr>
                );})}
                {!filteredCampaigns.length ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      No campaigns yet. Create one to generate short links and QR codes.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div
            ref={loadMoreSentinelRef}
            className="flex h-12 shrink-0 items-center justify-center"
            aria-hidden={!campaignsHasMore && !campaignsLoadingMore}
          >
            {campaignsLoadingMore ? (
              <span className="inline-flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading more…
              </span>
            ) : campaignsHasMore ? (
              <button
                type="button"
                onClick={() => void loadMoreCampaigns()}
                className={dlBtnGhostClass}
              >
                Load more
              </button>
            ) : filteredCampaigns.length > 0 ? (
              <span className="text-xs text-gray-400">All campaigns loaded</span>
            ) : null}
          </div>
        </div>
      ) : null}

      {tab === 'routes' ? (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                setRouteFormMode('create');
                setEditingRoute(null);
                setRouteFormOpen(true);
              }}
              className={dlBtnPrimaryClass}
            >
              <Plus className="h-4 w-4" /> Add route
            </button>
            <button
              type="button"
              onClick={() => void handleSeedRoutes()}
              className={dlBtnSecondaryClass}
            >
              Seed defaults
            </button>
            <button
              type="button"
              onClick={() => void handleSyncRegistry()}
              className={dlBtnSecondaryClass}
            >
              Sync from screen registry
            </button>
          </div>

          <div className={`${dlCardClass} p-4`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Screen registry</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  All eligible app destinations. <strong>Pending seed</strong> screens need a DB route — use Sync
                  from screen registry or create a campaign and pick the screen.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {DEEP_LINK_TARGET_APP_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setRegistryAppKey(option.value)}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${
                      registryAppKey === option.value
                        ? 'border-blue-600 bg-blue-50 text-blue-800 dark:border-blue-400 dark:bg-blue-950/40 dark:text-blue-200'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {screenCatalog?.summary ? (
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="rounded-full bg-gray-100 px-2 py-1 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                  {screenCatalog.summary.totalScreens} screens
                </span>
                <span className="rounded-full bg-emerald-100 px-2 py-1 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
                  {screenCatalog.summary.seededCount} seeded
                </span>
                <span className="rounded-full bg-amber-100 px-2 py-1 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                  {screenCatalog.summary.pendingCount} pending seed
                </span>
              </div>
            ) : null}

            {catalogLoading ? (
              <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading screen catalog…
              </div>
            ) : (
              <div className={`${dlTableWrapClass} mt-4`}>
                <table className="min-w-full text-sm">
                  <thead className={dlTableHeadClass}>
                    <tr>
                      <th className="px-4 py-3">Service</th>
                      <th className="px-4 py-3">Screen</th>
                      <th className="px-4 py-3">App route</th>
                      <th className="px-4 py-3">Web path</th>
                      <th className="px-4 py-3">DB status</th>
                      <th className="px-4 py-3">Slug</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registryScreens.map((screen) => (
                      <tr key={screen.screenName} className={dlTableRowClass}>
                        <td className={dlTableCellClass}>{screen.serviceLabel}</td>
                        <td className={dlTableCellClass}>{screen.label}</td>
                        <td className={`${dlTableCellClass} font-mono text-xs`}>{screen.appRoute}</td>
                        <td className={`${dlTableCellClass} font-mono text-xs`}>{screen.webPath || '—'}</td>
                        <td className={dlTableCellClass}>
                          <RouteSeedStatusBadge hasDbRoute={screen.hasDbRoute} />
                        </td>
                        <td className={`${dlTableCellClass} font-mono text-xs`}>{screen.slug}</td>
                      </tr>
                    ))}
                    {!registryScreens.length ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                          No eligible screens for this app.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <h3 className="font-semibold text-gray-900 dark:text-white">Routes in database</h3>
          <div className={dlTableWrapClass}>
            <table className="min-w-full text-sm">
              <thead className={dlTableHeadClass}>
                <tr>
                  <th className="px-4 py-3">Service</th>
                  <th className="px-4 py-3">Slug</th>
                  <th className="px-4 py-3">Label</th>
                  <th className="px-4 py-3">Screen name</th>
                  <th className="px-4 py-3">App route</th>
                  <th className="px-4 py-3">Web path</th>
                  <th className="px-4 py-3">Login</th>
                  <th className="px-4 py-3">App version</th>
                  <th className="px-4 py-3">Active</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {routes.map((route) => {
                  const service = route.screenName?.split(':')[2]?.replace(/_/g, ' ') || '—';
                  return (
                  <tr key={route.id} className={dlTableRowClass}>
                    <td className={`${dlTableCellClass} capitalize`}>{service}</td>
                    <td className={`${dlTableCellClass} font-mono text-xs`}>{route.slug}</td>
                    <td className={dlTableCellClass}>{route.label}</td>
                    <td className={`${dlTableCellClass} font-mono text-xs`}>{route.screenName || '—'}</td>
                    <td className={dlTableCellClass}>{route.appRoute || '—'}</td>
                    <td className={dlTableCellClass}>{route.webPath || '—'}</td>
                    <td className={dlTableCellClass}>{route.requiresAuth ? 'Yes' : 'No'}</td>
                    <td className={dlTableCellClass}>
                      {route.minAppVersion || route.maxAppVersion
                        ? `${route.minAppVersion || '*'} – ${route.maxAppVersion || '*'}`
                        : '—'}
                    </td>
                    <td className={dlTableCellClass}>{route.isActive ? 'Yes' : 'No'}</td>
                    <td className={dlTableCellClass}>
                      <div className="flex justify-end">{renderRouteActions(route)}</div>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {tab === 'analytics' ? (
        <div className="grid gap-6 lg:grid-cols-2">
          {summary?.clicksByPlatform ? (
            <div className={`${dlCardClass} p-4 lg:col-span-2`}>
              <h3 className="mb-4 font-semibold text-gray-900 dark:text-white">Opens by platform</h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  ['Web', summary.clicksByPlatform.WEB || 0],
                  ['Android', summary.clicksByPlatform.ANDROID || 0],
                  ['iOS', summary.clicksByPlatform.IOS || 0],
                  ['In-app', summary.clicksByPlatform.APP || 0],
                ].map(([label, value]) => (
                  <div key={String(label)} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                    <p className="text-xl font-semibold text-gray-900 dark:text-white">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <div className={`${dlCardClass} p-4`}>
            <h3 className="mb-4 font-semibold text-gray-900 dark:text-white">Conversion funnel</h3>
            {funnel ? (
              <>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={funnel.stages}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#2563eb" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-2 text-center text-sm sm:grid-cols-3">
                  <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800/80">
                    <p className="text-gray-500 dark:text-gray-400">Click → Install</p>
                    <p className="font-semibold text-gray-900 dark:text-white">{pct(funnel.rates.clickToInstall)}</p>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800/80">
                    <p className="text-gray-500 dark:text-gray-400">Install → Login</p>
                    <p className="font-semibold text-gray-900 dark:text-white">{pct(funnel.rates.installToLogin)}</p>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800/80">
                    <p className="text-gray-500 dark:text-gray-400">Login → Booking</p>
                    <p className="font-semibold text-gray-900 dark:text-white">{pct(funnel.rates.loginToBooking)}</p>
                  </div>
                </div>
              </>
            ) : null}
          </div>

          <div className={`${dlCardClass} p-4`}>
            <h3 className="mb-4 font-semibold text-gray-900 dark:text-white">Top campaigns by clicks</h3>
            <div className="space-y-3">
              {leaderboard.map((row) => (
                <div
                  key={row.campaignId}
                  className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700"
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{row.campaign?.name || row.campaignId}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{row.campaign?.routeSlug}</p>
                  </div>
                  <div className="text-right text-sm">
                    <p className="font-semibold text-gray-900 dark:text-white">{row.clicks} clicks</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {row.campaign?.totalBookings || 0} bookings
                    </p>
                  </div>
                </div>
              ))}
              {!leaderboard.length ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No click data yet.</p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      </div>

      <DeepLinkAppLinksModal
        open={appLinksOpen}
        onClose={() => setAppLinksOpen(false)}
        linkConfig={linkConfig}
      />

      <DeepLinkCampaignFormModal
        open={campaignFormOpen}
        mode={campaignFormMode}
        routes={routes.filter((r) => r.isActive)}
        initial={editingCampaign}
        onClose={() => setCampaignFormOpen(false)}
        onSubmit={campaignFormMode === 'create' ? handleCreateCampaign : handleUpdateCampaign}
        onRoutesChanged={() => void loadRoutes()}
      />

      <DeepLinkCampaignViewModal
        open={!!viewCampaign}
        campaign={viewCampaign}
        onClose={() => setViewCampaign(null)}
      />

      <DeepLinkCodeInspectModal
        open={codeLookupModalOpen}
        result={codeLookup}
        onClose={() => setCodeLookupModalOpen(false)}
      />

      <DeepLinkRouteFormModal
        open={routeFormOpen}
        mode={routeFormMode}
        initial={editingRoute}
        onClose={() => setRouteFormOpen(false)}
        onSubmit={routeFormMode === 'create' ? handleCreateRoute : handleUpdateRoute}
      />

      <ConfirmDialog
        open={confirm?.kind === 'delete-campaign'}
        title="Delete campaign?"
        message={
          confirm?.kind === 'delete-campaign' ? (
            <div className="space-y-2">
              <p>
                &quot;{confirm.campaign.name}&quot; will be permanently removed. Short links for this
                campaign will stop working.
              </p>
              {confirm.campaign.whatsappUsage?.used ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
                  <p className="font-medium">Used in WhatsApp</p>
                  <p className="mt-1 text-xs">
                    {formatWhatsAppUsageSummary(confirm.campaign.whatsappUsage)}. Deleting will break
                    those template variables / CTA links until reassigned.
                  </p>
                  {confirm.campaign.whatsappUsage.notifications.slice(0, 4).map((n) => (
                    <p key={n.eventKey} className="mt-1 truncate text-xs">
                      · {n.label} ({n.eventKey})
                    </p>
                  ))}
                  {confirm.campaign.whatsappUsage.templates.slice(0, 4).map((t) => (
                    <p key={t.id} className="mt-1 truncate text-xs">
                      · Template {t.displayName || t.name}
                    </p>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            ''
          )
        }
        tone="danger"
        confirmLabel={
          confirm?.kind === 'delete-campaign' && confirm.campaign.whatsappUsage?.used
            ? 'Delete anyway'
            : 'Delete'
        }
        busy={confirmBusy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => void handleConfirmAction()}
      />

      <ConfirmDialog
        open={confirm?.kind === 'pause-campaign'}
        title="Pause campaign?"
        message={
          confirm?.kind === 'pause-campaign'
            ? `"${confirm.campaign.name}" will be paused. Users opening its links may see an inactive or fallback experience until you activate it again.`
            : ''
        }
        tone="warning"
        confirmLabel="Pause campaign"
        busy={confirmBusy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => void handleConfirmAction()}
      />

      <ConfirmDialog
        open={confirm?.kind === 'delete-route'}
        title="Delete route?"
        message={
          confirm?.kind === 'delete-route'
            ? `Route "${confirm.route.slug}" will be removed from the registry. Existing campaigns using this route may break.`
            : ''
        }
        tone="danger"
        confirmLabel="Delete route"
        busy={confirmBusy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => void handleConfirmAction()}
      />
    </div>
  );
};

export default DeepLinksManagementPage;
